import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';

export interface DsmBlock {
  block: number;
  window: string;
  startMinutes: number;
  endMinutes: number;
}

@Injectable({ providedIn: 'root' })
export class DsmBlockService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly timeFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  private readonly currentBlockState = signal(this.getBlockAt());
  private timer: ReturnType<typeof setTimeout> | null = null;

  readonly currentBlock = this.currentBlockState.asReadonly();
  readonly currentBlockNumber = computed(() => this.currentBlock().block);
  readonly currentBlockWindow = computed(() => this.currentBlock().window);
  readonly timeZone = 'Asia/Kolkata';
  readonly blockWindows = Array.from(
    { length: 96 },
    (_, index) => this.createBlock(index + 1).window,
  );

  constructor() {
    this.scheduleNextUpdate();
    this.destroyRef.onDestroy(() => this.clearTimer());
  }

  getBlockAt(date = new Date()): DsmBlock {
    const { hours, minutes } = this.getTimeParts(date);
    const elapsedMinutes = hours * 60 + minutes;
    return this.createBlock(Math.floor(elapsedMinutes / 15) + 1);
  }

  private createBlock(block: number): DsmBlock {
    const startMinutes = (block - 1) * 15;
    const endMinutes = startMinutes + 15;
    return {
      block,
      window: `${this.formatTime(startMinutes)} - ${this.formatTime(endMinutes)}`,
      startMinutes,
      endMinutes,
    };
  }

  private scheduleNextUpdate(): void {
    this.clearTimer();
    const now = new Date();
    const { minutes } = this.getTimeParts(now);
    const elapsedInBlockMs =
      (minutes % 15) * 60_000 + now.getSeconds() * 1_000 + now.getMilliseconds();
    const nextBoundaryDelay = 15 * 60_000 - elapsedInBlockMs;
    this.timer = setTimeout(() => {
      this.currentBlockState.set(this.getBlockAt());
      this.scheduleNextUpdate();
    }, Math.max(1, nextBoundaryDelay));
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private formatTime(totalMinutes: number): string {
    const normalizedMinutes = totalMinutes % (24 * 60);
    const hours = Math.floor(normalizedMinutes / 60);
    const minutes = normalizedMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private getTimeParts(date: Date): { hours: number; minutes: number } {
    const parts = this.timeFormatter.formatToParts(date);
    return {
      hours: Number(parts.find((part) => part.type === 'hour')?.value ?? 0),
      minutes: Number(parts.find((part) => part.type === 'minute')?.value ?? 0),
    };
  }
}
